class SearchspringPredictiveSearch extends WiroHTMLRenderer {
  constructor() {
    super([SearchspringAPIModule, RequestUtilityModule]);
    this.lastSearchData = null; // Cache search data to check for redirect
  }

  get searchInput() {
    return this.querySelector("input");
  }
  get autocompleteContainer() {
    return this.querySelector("[data-autocomplete-container]");
  }
  get productsContainer() {
    return this.querySelector("[data-products-container]");
  }
  get showWithProducts() {
    return this.querySelectorAll("[data-show-with-products]");
  }
  get showWithAutocomplete() {
    return this.querySelectorAll("[data-show-with-autocomplete]");
  }
  get showWithQuery() {
    return this.querySelectorAll("[data-show-with-query]");
  }
  get hideWithQuery() {
    return this.querySelectorAll("[data-hide-with-query]");
  }
  get viewSearchContainer() {
    return this.querySelector("[data-view-search-container]");
  }
  get topSearchesContainer() {
    return this.querySelector("[data-top-searches-container]");
  }
  get closeButtons() {
    return this.querySelectorAll("[data-close]");
  }

  get isOpen() {
    return this.getAttribute("open");
  }
  set isOpen(value) {
    const announcementBar = document.querySelector(".section-announcement-bar");
    const headerMenu = document.querySelector(".header__inline-menu");
    if (!value) {
      if (announcementBar) this.style.removeProperty("top");
      if (headerMenu) headerMenu.style.removeProperty("display");
      this.removeAttribute("open");
      this.showWithQuery.forEach((el) => el.style.removeProperty("display"));
      this.hideWithQuery.forEach((el) => (el.style.display = "none"));
      this.searchInput.value = "";
      this.lastSearchData = null; // Clear cache
      this.clearSearchResults();
    } else {
      if (announcementBar) this.style.top = `${announcementBar.clientHeight}px`;
      if (headerMenu) headerMenu.style.display = "none";
      this.setAttribute("open", value);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.searchInputHandler = this.modules.requestUtility.debounce(this.handleSearchInput.bind(this));
    this.searchEnterHandler = this.handleSearchEnter.bind(this);
    this.searchInput.addEventListener("input", this.searchInputHandler);
    this.searchInput.addEventListener("keydown", this.searchEnterHandler);
    this.searchFocusHandler = this.handleInputFocus.bind(this);
    this.searchInput.addEventListener("focus", this.searchFocusHandler);
    this.closeHandler = this.close.bind(this);
    this.closeButtons.forEach((button) => button.addEventListener("click", this.closeHandler));
    this.autocompleteClickHandler = this.handleAutocompleteClick.bind(this);
    this.addEventListener("click", this.autocompleteClickHandler);
    this.fetchTopSearches();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.searchInput.removeEventListener("input", this.searchInputHandler);
    this.searchInput.removeEventListener("focus", this.searchFocusHandler);
    this.searchInput.removeEventListener("keydown", this.searchEnterHandler);
    this.closeButtons.forEach((button) => button.removeEventListener("click", this.closeHandler));
    this.removeEventListener("click", this.autocompleteClickHandler);
  }

  open() {
    this.isOpen = true;
  }
  close() {
    this.isOpen = false;
  }

  async handleSearchEnter(e) {
    if (e.key !== "Enter") return;
    e.preventDefault(); // Prevent default form submission

    const query = this.searchInput.value.trim();
    if (!query) return;

    // Check if we have redirect info from last search
    if (this.lastSearchData?.merchandising?.redirect) {
      window.location.href = this.lastSearchData.merchandising.redirect;
      return;
    }

    // If no cached data or no redirect, fetch fresh data
    try {
      const searchData = await this.modules.searchspring.sendSearchRequest([{ key: "q", value: query }]);

      if (searchData?.merchandising?.redirect) {
        window.location.href = searchData.merchandising.redirect;
        return;
      }
    } catch (error) {
      console.error("Error checking redirect:", error);
    }

    // No redirect found, proceed to search page
    this.visitSearchPage(query);
  }

  // Handle clicks on autocomplete suggestions and "View search" link
  async handleAutocompleteClick(e) {
    const target = e.target.closest('a[href*="/search?q="]');
    if (!target) return;

    e.preventDefault(); // Prevent default navigation

    const url = new URL(target.href);
    const query = url.searchParams.get("q");
    if (!query) {
      window.location.href = target.href;
      return;
    }

    // Check for redirect before navigating
    try {
      const searchData = await this.modules.searchspring.sendSearchRequest([{ key: "q", value: query }]);

      if (searchData?.merchandising?.redirect) {
        window.location.href = searchData.merchandising.redirect;
        return;
      }
    } catch (error) {
      console.error("Error checking redirect:", error);
    }

    // No redirect, proceed to search page
    window.location.href = target.href;
  }

  clearSearchResults() {
    this.autocompleteContainer.innerHTML = "";
    this.productsContainer.innerHTML = "";
    this.showWithAutocomplete.forEach((el) => (el.style.display = "none"));
    this.showWithProducts.forEach((el) => (el.style.display = "none"));
    this.showWithQuery.forEach((el) => (el.style.display = "none"));
    this.hideWithQuery.forEach((el) => el.style.removeProperty("display"));
    this.viewSearchContainer.innerHTML = "";
  }

  setSearch(string) {
    this.searchInput.value = string;
    this.handleSearchInput(string);
  }

  visitSearchPage(string) {
    window.location.href = `/search?q=${encodeURIComponent(string)}`;
  }

  handleInputFocus() {
    if (!this.isOpen) this.open();
  }

  async fetchTopSearches() {
    try {
      const siteId = this.modules.searchspring.siteId;
      const limit = 6;
      const response = await fetch(
        `https://${siteId}.a.searchspring.io/api/suggest/trending?siteId=${siteId}&limit=${limit}`,
      );
      const data = await response.json();
      this.renderTopSearches(data.trending?.queries || []);
    } catch (error) {
      console.error("Failed to fetch top searches:", error);
    }
  }

  renderTopSearches(queries) {
    if (!this.topSearchesContainer || !queries.length) return;
    this.topSearchesContainer.innerHTML = "";
    queries.forEach((query) => {
      const searchItem = this.prepareStringForRender(`
      <p class="tw-py-2 sm:tw-py-1 tw-capitalize">
        <a href="/search?q=${encodeURIComponent(query.searchQuery)}">${query.searchQuery}</a>
      </p>
    `);
      this.topSearchesContainer.appendChild(searchItem);
    });
  }

  async handleSearchInput(e) {
    const query = typeof e === "string" ? e : e.target.value;
    if (!query.length) {
      this.clearSearchResults();
      this.lastSearchData = null;
      return;
    }
    const autocompleteRequest = this.modules.searchspring.sendAutocompleteRequest(query);
    const searchRequest = this.modules.searchspring.sendSearchRequest([{ key: "q", value: query }]);
    const [autocompleteData, searchData] = await Promise.all([autocompleteRequest, searchRequest]);
    this.lastSearchData = searchData;
    const autocompleteSuggestions = autocompleteData.alternatives.map((alternative) => alternative.text);
    const matchingProducts = searchData.results.slice(0, 5);

    this.clearSearchResults();

    this.showWithQuery.forEach(
      (el) => (el.style.display = el.dataset.showWithQuery.length ? el.dataset.showWithQuery : "block"),
    );

    this.hideWithQuery.forEach((el) => (el.style.display = "none"));

    for (const suggestion of autocompleteSuggestions) {
      const autocompleteHighlightedCharactersStart = suggestion.toLowerCase().indexOf(query.toLowerCase());
      const suggestionSegments = [
        suggestion.slice(0, autocompleteHighlightedCharactersStart),
        suggestion.slice(autocompleteHighlightedCharactersStart, autocompleteHighlightedCharactersStart + query.length),
        suggestion.slice(autocompleteHighlightedCharactersStart + query.length),
      ];
      const suggestionDom = this.prepareStringForRender(`
        <p class="tw-py-2">
          <a href="/search?q=${encodeURIComponent(suggestion)}">${suggestionSegments[0]}<strong>${suggestionSegments[1]}</strong>${suggestionSegments[2]}</a>
        </p>
      `);
      this.autocompleteContainer.appendChild(suggestionDom);
    }
    if (autocompleteSuggestions.length) this.showWithAutocomplete.forEach((el) => (el.style.display = "block"));

    this.productCardComponent = "searchspring-predictive-search-card";
    for (const product of matchingProducts) {
      const card = document.createElement(this.productCardComponent);
      card.setProduct(product);
      this.productsContainer.appendChild(card);
    }
    if (matchingProducts.length) this.showWithProducts.forEach((el) => (el.style.display = "block"));

    const icon = this.dataset.viewAllResultsIcon;
    const viewSearchLink = this.prepareStringForRender(
      `<a class="tw-flex tw-justify-between" href="/search?q=${encodeURIComponent(query)}">
        <span>Search for "${query}"</span>
        <img src="${icon}" alt="Search">
      </a>`,
    );
    this.viewSearchContainer.appendChild(viewSearchLink);
  }
}
if (!customElements.get("searchspring-predictive-search"))
  customElements.define("searchspring-predictive-search", SearchspringPredictiveSearch);

class SearchspringPredictiveSearchCard extends WiroProductCard {
  constructor() {
    super();
  }
  setProduct(product) {
    this.product = product;
  }
  parseAPIProductData() {
    return {
      name: this.product.name,
      url: this.product.url,
      imageUrl: this.product.imageUrl,
      price: this.product.price,
    };
  }
  connectedCallback() {
    if (!this.product) return;
    const { name, url, imageUrl, price } = this.parseAPIProductData();

    const card = this.prepareStringForRender(`
      <a href="${url}">
        <div class="tw-flex tw-flex-row tw-mb-2 tw-items-center tw-h-20">
          <img src="${imageUrl}" alt="${name}" class="tw-bg-brand-secondary tw-h-full tw-rounded-[4px]">
          <div class="tw-flex tw-flex-col tw-p-4 tw-pl-3">
            <p class="tw-font-semibold tw-pb-1">${name}</p>
            <p>${this.formatPrice(price)}</p>
          </div>
        </div>
      </a>
    `);

    this.render(card);
  }
}
if (!customElements.get("searchspring-predictive-search-card"))
  customElements.define("searchspring-predictive-search-card", SearchspringPredictiveSearchCard);
